import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { useTranslation } from '../../context/LanguageContext';
import { Tag, Calendar, Sparkles, Bug, Zap, Package, RefreshCw, Globe, Loader2 } from 'lucide-react';
import { getReleases } from '@/services/releasesService';

/**
 * ReleasesPage - Changelog and version history
 * Loads releases from API with fallback to static data
 */
const ReleasesPage = () => {
    const { t } = useTranslation();
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fallback static releases data (in case API is not available)
    // Dates corrigées: +1 an (2024->2025, 2025->2026)
    const FALLBACK_RELEASES = [
        {
            version: 'v1.21',
            releasedate: '2026-01-24',
            releasetype: 'fix',
            title: 'Fix missing imports and replace Linkoo logo',
            description: 'Correction des imports manquants et remplacement du logo Linkoo par Cabaza.'
        },
        {
            version: 'v1.20',
            releasedate: '2026-01-23',
            releasetype: 'feature',
            title: 'Add mail test page and fix SQL scripts',
            description: 'Ajout de la page de test email et corrections des scripts SQL.'
        },
        {
            version: 'v1.19',
            releasedate: '2026-01-22',
            releasetype: 'feature',
            title: 'Add Time Tracking module (Phase 5)',
            description: 'Module de suivi du temps avec entrées, rapports et intégration projets.'
        },
        {
            version: 'v1.09',
            releasedate: '2026-01-21',
            releasetype: 'feature',
            title: 'Add Tasks module (Phase 4)',
            description: 'Module de gestion des tâches avec sous-tâches, commentaires et pièces jointes.'
        },
        {
            version: 'v0.99',
            releasedate: '2026-01-20',
            releasetype: 'feature',
            title: 'Add Skills module (Phase 3)',
            description: 'Module de compétences avec niveaux de maîtrise et intégration ESCO.'
        },
        {
            version: 'v0.89',
            releasedate: '2026-01-19',
            releasetype: 'feature',
            title: 'Add Projects module (Phase 2)',
            description: 'Module de gestion de projets avec jalons et membres.'
        },
        {
            version: 'v0.79',
            releasedate: '2026-01-18',
            releasetype: 'feature',
            title: 'Add Occupation Groups module (Phase 1)',
            description: 'Module de groupes de métiers pour catégorisation.'
        },
        {
            version: 'v0.69',
            releasedate: '2026-01-17',
            releasetype: 'refactor',
            title: 'Refactor module icons + error reporting',
            description: 'Refactoring des icônes de modules et ajout du rapport d\'erreurs automatique.'
        },
        {
            version: 'v0.66',
            releasedate: '2026-01-14',
            releasetype: 'feature',
            title: 'Add Expenses, Carpooling, Moments, Books modules',
            description: 'Ajout des modules Dépenses, Covoiturage, Moments et Bibliothèque.'
        },
        {
            version: 'v0.56',
            releasedate: '2026-01-13',
            releasetype: 'feature',
            title: 'Add Knowledge module',
            description: 'Ajout du module Knowledge base par métier.'
        },
        {
            version: 'v0.47',
            releasedate: '2026-01-12',
            releasetype: 'feature',
            title: 'Admin modules management + subscription profiles',
            description: 'Gestion des modules admin et types de profil par abonnement.'
        },
        {
            version: 'v0.46',
            releasedate: '2026-01-11',
            releasetype: 'feature',
            title: 'Add releases page with version history',
            description: 'Ajout de la page releases avec historique des versions.'
        },
        {
            version: 'v0.45',
            releasedate: '2026-01-10',
            releasetype: 'feature',
            title: t.releases?.v045 || 'Modules dynamiques et refonte des préférences',
            description: t.releases?.v045Desc || 'Chargement dynamique des modules depuis l\'API, restructuration des préférences avec onglets.'
        },
        {
            version: 'v0.44',
            releasedate: '2026-01-09',
            releasetype: 'fix',
            title: t.releases?.v044 || 'Corrections traductions et améliorations dashboard',
            description: t.releases?.v044Desc || 'Correction des traductions des dossiers au changement de langue.'
        },
        {
            version: 'v0.43',
            releasedate: '2026-01-08',
            releasetype: 'feature',
            title: t.releases?.v043 || 'Module Contacts: modal d\'édition',
            description: t.releases?.v043Desc || 'Ajout de EditContactModal et améliorations UI.'
        },
        {
            version: 'v0.40',
            releasedate: '2026-01-05',
            releasetype: 'feature',
            title: t.releases?.v040 || 'Module Produits',
            description: t.releases?.v040Desc || 'Ajout du module produits avec traductions i18n.'
        },
        {
            version: 'v0.35',
            releasedate: '2026-01-04',
            releasetype: 'performance',
            title: t.releases?.v035 || 'Optimisation du bundle',
            description: t.releases?.v035Desc || 'Suppression des dépendances inutilisées, code splitting.'
        },
        {
            version: 'v0.30',
            releasedate: '2025-12-30',
            releasetype: 'feature',
            title: t.releases?.v030 || 'Support multi-desktop',
            description: t.releases?.v030Desc || 'Ajout du support multi-desktop et correction du drag & drop.'
        },
        {
            version: 'v0.27',
            releasedate: '2025-12-27',
            releasetype: 'feature',
            title: t.releases?.v027 || 'Rebranding Cabaza',
            description: t.releases?.v027Desc || 'Renommage de Linkoo en Cabaza avec améliorations i18n.'
        },
        {
            version: 'v0.23',
            releasedate: '2025-12-23',
            releasetype: 'feature',
            title: t.releases?.v023 || 'Système d\'événements',
            description: t.releases?.v023Desc || 'Ajout du système d\'événements et améliorations calendrier.'
        },
        {
            version: 'v0.21',
            releasedate: '2025-12-21',
            releasetype: 'feature',
            title: t.releases?.v021 || 'Système de messagerie',
            description: t.releases?.v021Desc || 'Ajout du système de messagerie avec pièces jointes.'
        },
        {
            version: 'v0.15',
            releasedate: '2025-12-15',
            releasetype: 'refactor',
            title: t.releases?.v015 || 'Restructuration monorepo',
            description: t.releases?.v015Desc || 'Migration de frontend/ vers apps/ en structure monorepo.'
        },
        {
            version: 'v0.10',
            releasedate: '2025-12-10',
            releasetype: 'feature',
            title: t.releases?.v010 || 'React 19 Form Actions',
            description: t.releases?.v010Desc || 'Implémentation des React 19 Form Actions.'
        },
        {
            version: 'v0.07',
            releasedate: '2025-12-07',
            releasetype: 'feature',
            title: t.releases?.v007 || 'Migration React 19',
            description: t.releases?.v007Desc || 'Migration vers React 19 avec optimisations.'
        },
        {
            version: 'v0.05',
            releasedate: '2025-12-01',
            releasetype: 'milestone',
            title: t.releases?.v005 || 'Release stable v1 (interne)',
            description: t.releases?.v005Desc || 'Première version stable interne avant la release publique.'
        },
        {
            version: 'v0.01',
            releasedate: '2025-11-20',
            releasetype: 'milestone',
            title: t.releases?.v001 || 'Premier commit',
            description: t.releases?.v001Desc || 'Initialisation du projet Cabaza.'
        },
    ];

    // Load releases from API
    useEffect(() => {
        const loadReleases = async () => {
            try {
                setLoading(true);
                const data = await getReleases({ limit: 200 });

                if (data && data.length > 0) {
                    setReleases(data);
                } else {
                    // Fallback to static data if API returns empty
                    setReleases(FALLBACK_RELEASES);
                }
            } catch (err) {
                console.error('Error loading releases:', err);
                setError(err.message);
                // Fallback to static data on error
                setReleases(FALLBACK_RELEASES);
            } finally {
                setLoading(false);
            }
        };

        loadReleases();
    }, []);

    const getTypeIcon = (type) => {
        switch (type) {
            case 'feature':
                return <Sparkles className="w-4 h-4" />;
            case 'fix':
                return <Bug className="w-4 h-4" />;
            case 'performance':
                return <Zap className="w-4 h-4" />;
            case 'refactor':
                return <RefreshCw className="w-4 h-4" />;
            case 'i18n':
                return <Globe className="w-4 h-4" />;
            case 'milestone':
                return <Tag className="w-4 h-4" />;
            default:
                return <Package className="w-4 h-4" />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'feature':
                return 'bg-green-100 text-green-700';
            case 'fix':
                return 'bg-red-100 text-red-700';
            case 'performance':
                return 'bg-yellow-100 text-yellow-700';
            case 'refactor':
                return 'bg-blue-100 text-blue-700';
            case 'i18n':
                return 'bg-purple-100 text-purple-700';
            case 'milestone':
                return 'bg-gray-100 text-gray-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getTypeLabel = (type) => {
        const labels = {
            feature: t.releases?.typeFeature || 'Fonctionnalité',
            fix: t.releases?.typeFix || 'Correction',
            performance: t.releases?.typePerformance || 'Performance',
            refactor: t.releases?.typeRefactor || 'Refactoring',
            i18n: t.releases?.typeI18n || 'Traductions',
            milestone: t.releases?.typeMilestone || 'Jalon',
        };
        return labels[type] || type;
    };

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        // Handle ISO date format from API
        const date = new Date(dateStr);
        return date.toISOString().split('T')[0];
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            <main className="grow max-w-4xl mx-auto px-4 py-12 w-full">
                <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center">
                            <Tag className="w-5 h-5 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            {t.releases?.title || 'Notes de version'}
                        </h1>
                    </div>
                    <p className="text-gray-500 mb-8 ml-13">
                        {t.releases?.subtitle || 'Historique des mises à jour et améliorations de Cabaza'}
                    </p>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-cabaza-purple" />
                            <span className="ml-3 text-gray-500">
                                {t.common?.loading || 'Chargement...'}
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {releases.map((release, index) => (
                                <div
                                    key={release.version}
                                    className={`relative pl-8 pb-6 ${index !== releases.length - 1 ? 'border-l-2 border-gray-200' : ''}`}
                                >
                                    {/* Version dot */}
                                    <div className="absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-br from-cabaza-purple to-cabaza-pink border-4 border-white shadow"></div>

                                    <div className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <span className="font-mono font-bold text-cabaza-purple">
                                                {release.version}
                                            </span>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(release.releasetype || release.releaseType)}`}>
                                                {getTypeIcon(release.releasetype || release.releaseType)}
                                                {getTypeLabel(release.releasetype || release.releaseType)}
                                            </span>
                                            <span className="flex items-center gap-1 text-xs text-gray-400">
                                                <Calendar className="w-3 h-3" />
                                                {formatDate(release.releasedate || release.releaseDate)}
                                            </span>
                                        </div>
                                        <h3 className="font-semibold text-gray-900 mb-1">
                                            {release.title}
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {release.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-8 p-4 bg-purple-50 rounded-xl border border-purple-100">
                        <p className="text-sm text-purple-700">
                            <strong>{t.releases?.note || 'Note'}:</strong> {t.releases?.noteText || 'La version v1.00 marquera la première release publique stable de Cabaza.'}
                        </p>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default ReleasesPage;
